import ConfidenceIntervalCard from "@components/Scenario/ConfidenceIntervalCard";
import KpiBudgetCard from "@components/Scenario/KpiBudgetCard";
import KpiBudgetCharts from "@components/Scenario/KpiBudgetCharts";
import { ClientContext } from "@context/Client/ClientContext";
import {
  Box,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { useClientModelInputs, useScenarioSimulation } from "@services/api";
import {
  ClientModelVariable,
  CustomClientModelVariable,
  ModelCreateScenario,
  ModelScenarioInput,
  ModelScenarioOutput,
  ScenarioSimulation
} from "@services/api/types";
import { IVariableValues } from "@utils/external_effects";
import { DateTime } from "luxon";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import StepSimulationSkeleton from "./StepSimulationSkeleton";

const _getModelVarMapping = (
  modelVars: ClientModelVariable[]
): { [key: number]: string } => {
  const mapping: { [key: number]: string } = {};
  (modelVars || []).forEach((modelVar) => {
    mapping[modelVar.id] = modelVar.name;
  });
  return mapping;
};

export interface StepSimulationProps {
  timePeriods: string[];
  externalValues: IVariableValues;
  mediaValues: IVariableValues;
  mediaVars: CustomClientModelVariable[];
  extVars: ClientModelVariable[];
  setNewScenario: Dispatch<SetStateAction<ModelCreateScenario>>;
}
const StepSimulation = ({
  timePeriods,
  externalValues,
  mediaValues,
  mediaVars,
  extVars,
  setNewScenario
}: StepSimulationProps) => {
  const { currentClient } = useContext(ClientContext);
  const [scName, setScName] = useState("");
  const [scDescription, setScDescription] = useState("");
  const [isFavorite, setIsFavorite] = useState(false);

  const { t } = useTranslation();

  const { data: budget, isFetching: isLoadingInflow } = useClientModelInputs(
    currentClient.model.id.toString(),
    ["budget"],
    timePeriods
  );

  const extVarMapping = useMemo(() => _getModelVarMapping(extVars), [extVars]);

  const mediaVarMapping = useMemo(
    () => _getModelVarMapping(mediaVars),
    [mediaVars]
  );

  const {
    payload,
    totalMediaInvestments,
    scenarioInputs
  }: {
    payload: ScenarioSimulation;
    totalMediaInvestments: number;
    scenarioInputs: ModelScenarioInput[];
  } = useMemo(() => {
    const mediaInvestments: { [key: string]: { [key: string]: number } } = {};
    const externalInvestments: { [key: string]: { [key: string]: number } } =
      {};
    let totalMediaInvestments = 0;
    const scenarioInputs: ModelScenarioInput[] = [];

    timePeriods.forEach((timePeriod) => {
      externalInvestments[timePeriod] = {};
      mediaInvestments[timePeriod] = {};

      Object.keys(extVarMapping).forEach((orderId) => {
        const externalId = Number(orderId);
        const varName = extVarMapping[externalId];
        externalInvestments[timePeriod][varName] =
          externalValues[timePeriod][externalId];
        scenarioInputs.push({
          name: varName,
          time_period: timePeriod,
          value: externalValues[timePeriod][externalId]
        });
      });

      Object.keys(mediaVarMapping).forEach((orderId) => {
        const externalId = Number(orderId);
        const varName = mediaVarMapping[externalId];
        mediaInvestments[timePeriod][varName] =
          mediaValues[timePeriod][externalId];
        totalMediaInvestments += mediaValues[timePeriod][externalId];

        scenarioInputs.push({
          name: varName,
          time_period: timePeriod,
          value: mediaValues[timePeriod][externalId]
        });
      });
    });

    return {
      payload: {
        model_id: Number(currentClient.model.id),
        prediction_dates: timePeriods,
        prediction_length: timePeriods.length,
        investment_values: mediaInvestments,
        external_values: externalInvestments
      },
      totalMediaInvestments,
      scenarioInputs
    };
  }, [
    currentClient.model.id,
    extVarMapping,
    mediaVarMapping,
    externalValues,
    mediaValues,
    timePeriods
  ]);

  const { data: simulation, isFetching: isLoadingSimulation } =
    useScenarioSimulation(payload);

  const isLoading = useMemo(
    () => isLoadingInflow || isLoadingSimulation,
    [isLoadingInflow, isLoadingSimulation]
  );

  const dataOutputs: ModelScenarioOutput[] = useMemo(
    () =>
      timePeriods.map((timePeriod, idx) => ({
        name: "kpi",
        time_period: timePeriod,
        value: simulation?.inflow_breakdown[idx] ?? 0
      })),
    [timePeriods, simulation]
  );

  const onFavorite = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsFavorite(event.target.checked);
  };

  useEffect(() => {
    const targetInflow = (budget || []).reduce(
      (acc, budgetWeek) => acc + budgetWeek.value,
      0
    );
    const timePeriodSorted = timePeriods.sort((a, b) => a.localeCompare(b));
    const timePeriodFmt = `${timePeriodSorted[0]} - ${timePeriodSorted[timePeriods.length - 1]}`;
    const newScenario: ModelCreateScenario = {
      scenario: {
        id: undefined,
        model_id: Number(currentClient.model.id),
        name: scName,
        description: scDescription,
        time_period: timePeriodFmt,
        year: DateTime.now().year,
        media_investment: totalMediaInvestments,
        predicted_inflow: simulation?.total_inflow ?? 0,
        target_inflow: targetInflow,
        is_favorite: isFavorite,
        forecast_selected: false,
        robustness_index: simulation?.confidence,
        inputs: [],
        outputs: []
      },
      inputs: scenarioInputs,
      outputs: dataOutputs
    };
    setNewScenario(newScenario);
  }, [
    setNewScenario,
    currentClient.model.id,
    scName,
    scDescription,
    isFavorite,
    timePeriods,
    dataOutputs,
    scenarioInputs,
    totalMediaInvestments,
    simulation,
    budget
  ]);

  return (
    <Box className="w-full mt-8">
      {isLoading ? (
        <StepSimulationSkeleton
          cardsCount={currentClient.client.mmm_model_config.metrics.length}
          key={`${currentClient.model.id}-step-simulation-skeleton`}
        />
      ) : (
        <Box className="w-full ml-4 pr-8">
          <Box className="w-full">
            <KpiBudgetCard
              timePeriods={timePeriods}
              outputs={dataOutputs}
              budget={budget || []}
            />
            <ConfidenceIntervalCard
              confidenceInterval={simulation?.confidence ?? 0}
            />
            <KpiBudgetCharts
              timePeriods={timePeriods}
              outputs={dataOutputs}
              budget={budget || []}
              media_investment={totalMediaInvestments}
            />
          </Box>
          <Divider className="pt-12" variant="middle" component="div" />
          <Box className="w-full mt-8 pl-4">
            <Typography variant="h6" component="div" sx={{ fontSize: 16 }}>
              {t("simulator.new_scenario.general_information")}
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ marginTop: 2 }}>
                <Box className="w-full mt-4">
                  <TextField
                    type="text"
                    variant="outlined"
                    label={t("simulator.new_scenario.scenario_name")}
                    fullWidth
                    value={scName}
                    onChange={(event) => {
                      setScName(event.target.value as string);
                    }}
                    required
                  />
                </Box>
                <Box className="w-full mt-4">
                  <TextField
                    type="text"
                    fullWidth
                    variant="outlined"
                    label={t("simulator.new_scenario.scenario_description")}
                    multiline
                    value={scDescription}
                    onChange={(event) => {
                      setScDescription(event.target.value as string);
                    }}
                    required
                  />
                </Box>
                <Box className="w-full mt-4">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isFavorite}
                        onChange={onFavorite}
                        color="primary"
                      />
                    }
                    label={t("simulator.new_scenario.favorite")}
                    labelPlacement="start"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "1rem",
                        fontWeight: 500
                      }
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default StepSimulation;
